import s from "./NewMemberForm.module.scss";
import clsx from "clsx";
import { Button } from "antd";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import useSWRMutation from "swr/mutation";
import {
  parseClientError,
  swrFetcher,
  swrMutationFetcher,
  SwrMutationFetcherArgType,
} from "../../../../../../libs/api/Endpoints";
import { UseAuthToken } from "../../../../../../libs/api/userSession";
import "react-phone-input-2/lib/style.css";
import useSWR from "swr";
import SelectField from "../../../../../../global components/Forms/SelectField/SelectField";
import {
  PaginatedTeamResponse,
  Roles,
} from "../../../../../../libs/Utils/Types/Teams";
import { SuccessDataType } from "../../../../../../libs/api/types";
import { useEffect, useState } from "react";
import { generateResult } from "../../../../../../global components/ResultModal/ResultModal";
import Unauthorised from "../../../../../../global components/Unauthorised";

type Inputs = {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
};

type AddContactProp = {
  handleOpen: () => void;
  groupID?: string | null;
  mutate?: () => void;
};

function NewMemberForm({ handleOpen }: AddContactProp) {
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<Inputs>({ shouldFocusError: false });
  const [searchValue /*setSearchValue*/] = useState("");
  const [currentPage /*setCurrentPage*/] = useState(1);
  const [pageItems /*setPageItems*/] = useState(10);
  const [, /*filter*/ setFilter] = useState<string>("");

  const [unauthorised, setUnauthorised] = useState<
    boolean | { title: string; message: string }
  >(false);
  const token = UseAuthToken();

  const { data: roles, error } = useSWR<{ data: Roles[] }, any, any>(
    token ? ["api/admin/team/roles", token] : null,
    swrFetcher,
    {
      revalidateOnFocus: true,
      shouldRetryOnError: false, // Disable retrying on errors
    },
  );
  const { trigger: inviteMember, isMutating } = useSWRMutation(
    "api/admin/team/members/invite",
    swrMutationFetcher,
  );
  const { mutate: mutateTeam } = useSWR<PaginatedTeamResponse, any, any>(
    token
      ? [
          "api/admin/team/members",
          token,
          { page: currentPage, limit: pageItems, search: searchValue },
        ]
      : null,
    swrFetcher,
    {
      shouldRetryOnError: false, // Disable retrying on errors
    },
  );
  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    try {
      if (token) {
        const fetchArg: SwrMutationFetcherArgType = {
          token: token,
          requestOption: {
            method: "post",
            body: JSON.stringify({
              firstName: data.firstName,
              lastName: data.lastName,
              email: data.email,
              roleId: data.role,
            }),
            headers: {
              "Content-Type": "application/json",
            },
          },
        };
        const result: SuccessDataType<any> = await inviteMember(fetchArg);
        handleOpen();
        generateResult(
          "success",
          result.message.charAt(0).toUpperCase() + result.message.slice(1),
        );
        mutateTeam();
        reset();
      }
    } catch (error: any) {
      const e = parseClientError(error);
      generateResult(
        "error",
        e.message.charAt(0).toUpperCase() + e.message.slice(1) || e.error,
      );
      console.error(e);
    }
  };
  useEffect(() => {
    if (error) {
      const e = parseClientError(error);
      if (e.statusCode === 403) {
        setUnauthorised(true);
      } else {
        setUnauthorised({
          title: "Error!",
          message: e.message || "Something went wrong",
        });
      }
    }
  }, [error]);
  useEffect(() => {
    if (roles) {
      setFilter(roles.data[0].id);
      setValue("role", roles.data[0].id);
    }
  }, [roles, setValue]);
  return unauthorised ? (
    typeof unauthorised === "boolean" ? (
      <div className={s.spin}>
        <Unauthorised />
      </div>
    ) : (
      <div className={s.spin}>
        <Unauthorised error={unauthorised} />
      </div>
    )
  ) : (
    <div className={s.wrapper}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={s.forms}>
          <div className={clsx(s.inputWrapper)}>
            <label htmlFor="firstName" className={clsx(s.inputLabel)}>
              First name
            </label>
            <input
              id="firstName"
              className={clsx(s.input, { [s.inputError]: errors.firstName })}
              {...register("firstName")}
            />
            {errors.firstName && (
              <span className={clsx(s.errorMessage)}>
                {errors.firstName.message}
              </span>
            )}
          </div>
          <div className={clsx(s.inputWrapper)}>
            <label htmlFor="lastName" className={clsx(s.inputLabel)}>
              Last name
            </label>
            <input
              id="lastName"
              className={clsx(s.input, { [s.inputError]: errors.lastName })}
              {...register("lastName")}
            />
            {errors.lastName && (
              <span className={clsx(s.errorMessage)}>
                {errors.lastName.message}
              </span>
            )}
          </div>

          <div className={clsx(s.inputWrapper)}>
            <label htmlFor="email" className={clsx(s.inputLabel)}>
              Email address
            </label>
            <input
              id="email"
              className={clsx(s.input, { [s.inputError]: errors.email })}
              {...register("email", { required: "Email is required" })}
            />
            {errors.email && (
              <span className={clsx(s.errorMessage)}>
                {errors.email.message}
              </span>
            )}
          </div>

          <div className={s.inputWrapper}>
            <h5 className={s.inputLabel}>Role</h5>
            <Controller
              name="role"
              control={control}
              rules={{ required: "Role is required" }}
              render={({ field }) => (
                <SelectField
                  defaultValue={roles && roles?.data[0].id}
                  {...field}
                  bordered
                  onChange={field.onChange}
                  defaultActiveFirstOption={true}
                  wrapperClassname={s.SelectWrapper}
                  className={clsx(s.selectInput, {
                    [s.inputError]: errors.role,
                  })}
                  options={roles?.data.map((role) => ({
                    value: role.id,
                    label: role.title,
                  }))}
                />
              )}
            />

            {errors.role && (
              <span className={clsx(s.errorMessage)}>
                {errors.role.message}
              </span>
            )}
          </div>
        </div>
        <div className={s.addContactBtns}>
          <Button
            type="default"
            className={clsx(s.button, s.cancelBtn)}
            onClick={handleOpen}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className={clsx(s.button, s.addContactBtn)}
            htmlType="submit"
            loading={isMutating}
            disabled={isMutating}
          >
            Invite member
          </Button>
        </div>
      </form>
    </div>
  );
}

export default NewMemberForm;
